import { BaseTable, Button, Card, TableHeader, TableNumber } from "components"
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io"
import { convertNumber } from "utils"
import type { ItemListSummaryRow, SortKey, SortState } from "../types/listItemSummary"
import {
  inventoryValue,
  marginPercent
} from "../utils/listItemSummaryHelpers"

type SummaryTableProps = {
  rows: ItemListSummaryRow[]
  sort: SortState
  onToggleSort: (key: SortKey) => void
  onExportXls: () => void
  onExportPdf: () => void
  onPrint: () => void
  onViewItem: (row: ItemListSummaryRow) => void
}

function SortTh({
  label,
  skey,
  sort,
  onToggle,
  align
}: {
  label: string
  skey: SortKey
  sort: SortState
  onToggle: (k: SortKey) => void
  align?: "left" | "right" | "center"
}) {
  const active = sort.key === skey
  const flex =
    align === "right"
      ? "justify-end"
      : align === "center"
        ? "justify-center"
        : "justify-start"
  return (
    <th
      className="cursor-pointer select-none"
      onClick={() => onToggle(skey)}>
      <div className={`flex items-center gap-1.5 ${flex}`}>
        <span className="leading-tight">{label}</span>
        {active ? (
          sort.asc ? (
            <IoIosArrowUp className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <IoIosArrowDown className="h-4 w-4 shrink-0" aria-hidden />
          )
        ) : null}
      </div>
    </th>
  )
}

export function SummaryTable({
  rows,
  sort,
  onToggleSort,
  onExportXls,
  onExportPdf,
  onPrint,
  onViewItem
}: SummaryTableProps) {
  const n = rows.length
  const page = 1

  return (
    <Card className="shadow-sm">
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b border-[#dadada] px-4 py-3">
        <h3 className="text-sm font-bold uppercase tracking-wide text-base-content sm:text-base">
          Item List Summary Report
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            color="outline-secondary"
            size="sm"
            onClick={onExportXls}>
            Export XLS
          </Button>
          <Button
            type="button"
            color="outline-secondary"
            size="sm"
            onClick={onExportPdf}>
            Export PDF
          </Button>
          <Button
            type="button"
            color="outline-secondary"
            size="sm"
            onClick={onPrint}>
            Print
          </Button>
        </div>
      </div>

      <Card.Body className="p-0">
        <div className="max-h-[min(70vh,calc(100vh-380px))] min-h-[200px] overflow-x-auto overflow-y-auto">
          <BaseTable className="border-collapse text-sm [&_td]:align-middle [&_th]:align-middle">
            <TableHeader className="sticky top-0 z-[3]">
              <tr>
                <th>NO</th>
                <SortTh
                  label="ITEM CODE"
                  skey="code"
                  sort={sort}
                  onToggle={onToggleSort}
                />
                <SortTh
                  label="ITEM NAME"
                  skey="name"
                  sort={sort}
                  onToggle={onToggleSort}
                />
                <SortTh
                  label="CATEGORY"
                  skey="category"
                  sort={sort}
                  onToggle={onToggleSort}
                />
                <SortTh
                  label="LOCATION"
                  skey="location"
                  sort={sort}
                  onToggle={onToggleSort}
                />
                <th className="text-center">UOM</th>
                <SortTh
                  label="SOH"
                  skey="soh"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                />
                <SortTh
                  label="AVERAGE COST"
                  skey="avgCost"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                />
                <SortTh
                  label="LAST PRICE"
                  skey="lastPrice"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                />
                <SortTh
                  label="SELLING PRICE"
                  skey="sellingPrice"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                />
                <SortTh
                  label="INVENTORY VALUE"
                  skey="inventoryValue"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                />
                <SortTh
                  label="MARGIN %"
                  skey="margin"
                  sort={sort}
                  onToggle={onToggleSort}
                  align="right"
                />
                <SortTh
                  label="STOCK STATUS"
                  skey="stockStatus"
                  sort={sort}
                  onToggle={onToggleSort}
                />
                <SortTh
                  label="MOVEMENT STATUS"
                  skey="movementStatus"
                  sort={sort}
                  onToggle={onToggleSort}
                />
                <th className="text-center">ACTION</th>
              </tr>
            </TableHeader>

            <tbody>
              {n === 0 ? (
                <tr>
                  <td colSpan={15} className="text-center">
                    No data available
                  </td>
                </tr>
              ) : null}
              {rows.map((row, index) => {
                const inv = inventoryValue(row)
                const margin = marginPercent(row)
                const marginLabel =
                  margin !== null ? `${margin.toFixed(1)}%` : "-"

                return (
                  <tr key={row.itemCode}>
                    <td>
                      {TableNumber({
                        dataLength: n,
                        pageCurrent: page,
                        index,
                        limit: n,
                        totalData: n
                      })}
                    </td>
                    <td className="whitespace-nowrap font-medium">
                      {row.itemCode}
                    </td>
                    <td className="max-w-[12rem] truncate sm:max-w-[16rem] md:max-w-[20rem]" title={row.itemName}>
                      {row.itemName}
                    </td>
                    <td className="max-w-[8rem] truncate">{row.category}</td>
                    <td className="max-w-[8rem] truncate">{row.location}</td>
                    <td className="text-center">{row.uom}</td>
                    <td className="text-right">
                      {convertNumber(Number(row.soh) || 0).withoutDecimal}
                    </td>
                    <td className="text-right">
                      {convertNumber(Number(row.avgCost) || 0).intoCurrency}
                    </td>
                    <td className="text-right">
                      {convertNumber(Number(row.lastPrice) || 0).intoCurrency}
                    </td>
                    <td className="text-right">
                      {convertNumber(Number(row.sellingPrice) || 0).intoCurrency}
                    </td>
                    <td className="text-right">
                      {convertNumber(inv).intoCurrency}
                    </td>
                    <td className="text-right">{marginLabel}</td>
                    <td>{row.stockStatus}</td>
                    <td>{row.movementStatus}</td>
                    <td className="text-center">
                      <Button
                        type="button"
                        color="primary"
                        size="sm"
                        onClick={() => onViewItem(row)}>
                        View
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </BaseTable>
        </div>
      </Card.Body>
    </Card>
  )
}
